import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Calendar, MapPin, Heart, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { language, t } = useLanguage();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Event posters for gallery
  const eventPosters = [
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/3jl6jyfc_Plakat.jpg',
      title_de: 'Cinemavisione: C\'è ancora domani',
      title_it: 'Cinemavisione: C\'è ancora domani',
    },
    {
      id: 4,
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/umpwqoed_Plakat%20Apericena%20Kopie.jpg',
      title_de: 'Apericena mediterranea',
      title_it: 'Apericena mediterranea',
    },
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % eventPosters.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [eventPosters.length]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events/upcoming`);
        const data = Array.isArray(response.data) ? response.data : [];
        setUpcomingEvents(data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#F5F5F7]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-2xl">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <img 
                src="/logo-bunt.jpg" 
                alt="Amitalia e.V." 
                className="h-40 md:h-52 w-auto"
              />
            </motion.div>

            {/* Founded Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-8"
            >
              <span className="year-badge">{t('founded')} 2011</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6"
              data-testid="hero-title"
            >
              {t('hero_slogan')}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-foreground/70 mb-6 leading-relaxed"
            >
              {t('hero_subtitle')}
            </motion.p>
          </div>

          {/* Event Gallery Carousel - Inside Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mt-8 mb-10"
          >
            <div className="relative h-[350px] md:h-[450px] flex items-center justify-center">
              <AnimatePresence mode="wait">
                {eventPosters.map((poster, index) => (
                  index === currentSlide && (
                    <motion.div
                      key={poster.id}
                      initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                      exit={{ opacity: 0, scale: 0.8, rotateY: 15 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      className="absolute"
                    >
                      <div className="relative luxury-shadow">
                        <img
                          src={poster.image}
                          alt={language === 'de' ? poster.title_de : poster.title_it}
                          className="h-[300px] md:h-[400px] w-auto object-contain"
                        />
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + eventPosters.length) % eventPosters.length)}
                className="absolute left-0 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 md:p-3 rounded-full luxury-shadow transition-all hover:scale-110 z-10"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % eventPosters.length)}
                className="absolute right-0 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 md:p-3 rounded-full luxury-shadow transition-all hover:scale-110 z-10"
                data-testid="carousel-next"
              >
                <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-3 mt-6">
              {eventPosters.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'bg-ferrari w-6' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <Link
                to="/membership"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="hero-cta-membership"
              >
                {t('hero_cta')}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/events"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="hero-cta-events"
              >
                {t('hero_events')}
              </Link>
            </motion.div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-1 gradient-line" />
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#F5F5F7]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
            {[
              { number: '14+', label: language === 'de' ? 'Jahre Erfahrung' : 'Anni di esperienza', icon: Calendar },
              { number: '2', label: language === 'de' ? 'Kulturen, eine Leidenschaft' : 'Culture, una passione', icon: Heart },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-4 text-ferrari" />
                <div className="font-heading text-3xl md:text-4xl font-semibold mb-2">{stat.number}</div>
                <div className="text-sm text-foreground/60">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events Preview */}
      {upcomingEvents.length > 0 && (
        <section className="py-20" data-testid="upcoming-events-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex justify-between items-end mb-12">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
                  {t('events_upcoming')}
                </h2>
                <p className="text-foreground/60">
                  {language === 'de' ? 'Entdecken Sie unsere nächsten Veranstaltungen' : 'Scopri i nostri prossimi eventi'}
                </p>
              </div>
              <Link
                to="/events"
                className="hidden md:flex items-center gap-2 text-ferrari font-semibold hover:gap-3 transition-all"
              >
                {language === 'de' ? 'Alle Veranstaltungen' : 'Tutti gli eventi'}
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="event-card bg-white border border-black/5 overflow-hidden"
                  data-testid={`event-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={event.image_url || '/placeholder-event.svg'}
                      alt={language === 'de' ? event.title_de : event.title_it}
                      className="card-image w-full h-full object-cover transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-foreground/60 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(event.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'it-IT')}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {event.location}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">
                      {language === 'de' ? event.title_de : event.title_it}
                    </h3>
                    <p className="text-sm text-foreground/60 line-clamp-2">
                      {language === 'de' ? event.description_de : event.description_it}
                    </p>
                  </div>
                  <div className="card-border h-1 bg-gray-200 transition-colors duration-300" />
                </motion.div>
              ))}
            </div>

            <div className="mt-8 text-center md:hidden">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 text-ferrari font-semibold"
              >
                {language === 'de' ? 'Alle Veranstaltungen' : 'Tutti gli eventi'}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A] text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              {language === 'de' ? 'Werden Sie Teil unserer Gemeinschaft' : 'Entra a far parte della nostra comunità'}
            </h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              {language === 'de'
                ? 'Verbinden Sie sich mit Menschen, die Ihre Leidenschaft für beide Kulturen teilen.'
                : 'Connettiti con persone che condividono la tua passione per entrambe le culture.'}
            </p>
            <Link
              to="/membership"
              className="btn-primary bg-ferrari hover:bg-white hover:text-foreground inline-flex items-center gap-2"
              data-testid="cta-membership-btn"
            >
              {t('hero_cta')}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
